import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { CasesFilters } from "@/components/cases/cases-filters";
import { CasesTable } from "@/components/cases/cases-table";
import { useCasesSearch } from "@/hooks/use-cases-search";
import { useExport } from "@/hooks/use-export";
import { casesExportColumns } from "@/lib/export/columns";
import { useRouter } from "@/i18n/navigation";
import type { ApiSearchCaseItem } from "@/types/api-helpers";

export const Route = createFileRoute("/_dashboard/cases/")({ component: CasesPage });

function CasesContent() {
  const t = useTranslations("CasesPage");
  const tCaseStatus = useTranslations("CaseStatus");
  const tCasesTable = useTranslations("CasesTable");
  const locale = useLocale();
  const router = useRouter();
  const {
    data,
    isLoading,
    search,
    setSearch,
    status,
    setStatus,
    type,
    setType,
    source,
    setSource,
    assigned,
    setAssigned,
    createdAfter,
    setCreatedAfter,
    createdBefore,
    setCreatedBefore,
    sorting,
    onSortingChange,
    paginationState,
    onPaginationChange,
    pageCount,
  } = useCasesSearch();

  const exportFilters = useMemo(() => ({
    ...(search ? { q: search } : {}),
    ...(status !== "all" ? { status } : {}),
    ...(type !== "all" ? { type_id: type } : {}),
    ...(source !== "all" ? { source_id: source } : {}),
    ...(assigned !== "all" ? { assigned_user_id: assigned } : {}),
    ...(createdAfter ? { created_after: `${createdAfter}T00:00:00Z` } : {}),
    ...(createdBefore ? { created_before: `${createdBefore}T23:59:59Z` } : {}),
  }), [search, status, type, source, assigned, createdAfter, createdBefore]);

  const { exportData, progress: exportProgress, cancel: cancelExport } = useExport<ApiSearchCaseItem>({
    endpoint: "/admin/search/cases",
    columns: casesExportColumns,
    filters: exportFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headerTranslator: (key) => tCasesTable(key as any),
    translators: {
      locale,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tStatus: (key) => tCaseStatus(key as any),
    },
    filenamePrefix: t("exportFilename"),
    pdfTitle: t("title"),
  });

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <CasesFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          type={type}
          onTypeChange={setType}
          source={source}
          onSourceChange={setSource}
          assigned={assigned}
          onAssignedChange={setAssigned}
          createdAfter={createdAfter}
          onCreatedAfterChange={setCreatedAfter}
          createdBefore={createdBefore}
          onCreatedBeforeChange={setCreatedBefore}
          searchPlaceholder={t("searchPlaceholder")}
          onExport={exportData}
          exportProgress={exportProgress}
          onExportCancel={cancelExport}
        />
        <CasesTable
          data={data}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={paginationState}
          onPaginationChange={onPaginationChange}
          pageCount={pageCount}
          isLoading={isLoading}
          onRowClick={(row) => router.push(`/cases/${row.id}`)}
        />
      </div>
    </>
  );
}

function CasesPage() {
  return (
    <Suspense>
      <CasesContent />
    </Suspense>
  );
}
